import { Typography, Input, Box, ListItemButton, Sheet, List, CircularProgress, FormControl, FormHelperText, Stack } from '@mui/joy';
import { WarningCircle } from '@phosphor-icons/react';
import { AppProfile } from '../../../components/app/App';
import LayoutSettings from '../../../components/layout/LayoutSettings';
import { CaretRight } from '@phosphor-icons/react';
import { useProfile } from '../../../hooks/profile/useProfile';
import { useEffect, useRef, useState } from 'react';
import ServerError from '../../../components/module/ServerError/ServerError';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/apiClient';
import HorizontalScrollAppearanceList from './Appearance/HorizontalScrollAppearanceList';
import ImageOptionAvatar from './Appearance/Option/ImageOptionAvatar';
import ImageOptionLoadAvatar from './Appearance/Option/ImageOptionLoadAvatar';
import ImageOptionCap from './Appearance/Option/ImageOptionCap';
import ImageOptionLoadCap from './Appearance/Option/ImageOptionLoadCap';
import { convertHeicToJpg } from '../../../utils/convertHeicToJpg';
import AvatarCropModal from './Appearance/Crop/AvatarCropModal';
import CapCropModal from './Appearance/Crop/CapCropModal';
import defaultAvatars from '../../../data/userImages/defaultAvatars';
import defaultCovers from '../../../data/userImages/defaultCovers';
import ModalConfirmDelete from '../../../components/module/ModalConfirmDelete/ModalConfirmDelete';
import ProfileLayoutHint from '../../../components/module/ProfileLayoutHint/ProfileLayoutHint'

const uploadCroppedImage = async (file, url, setErrorAvatar) => {
    try {
        setErrorAvatar('');

        const formData = new FormData();
        formData.append('files', file);

        const res = await apiFetch(url, {
            method: 'PATCH',
            body: formData,
        });

        if (!res.ok) throw new Error('Ошибка при загрузки аватарки');

        const data = await res.json();

        if (data?.status) {
            return data?.image;
        }
        else {
            throw new Error('Ошибка при загрузки аватарки');
        }
    } catch (e) {
        setErrorAvatar('Ошибка при загрузки аватарки');
    }
}

const fetchGetImages = async (link, setImages, setLoading, setError) => {
    setLoading(true);

    try {
        const accessToken = localStorage.getItem('accessToken')
        if (!accessToken) return;

        const res = await apiFetch(link, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
        });

        const data = await res.json();
        if (!data?.status) {
            setError(data?.error);
        }
        else {
            setImages(data?.images ?? []);
        }
    } catch (err) {

    }
    finally {
        setLoading(false);
    }
}

const fetchUpdateImage = async (link, body, setError = () => { }) => {
    try {
        const accessToken = localStorage.getItem('accessToken')
        if (!accessToken) return;

        const res = await apiFetch(link, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: body,
        });

        if (!res.ok) setError('Не удалось обновить');

        const data = await res.json();
        if (!data?.status) setError(data?.error);
    } catch (err) {

    }
}

const deleteImage = async (link, body, setError = () => { }) => {
    try {
        const accessToken = localStorage.getItem('accessToken')
        if (!accessToken) return false;

        const res = await apiFetch(link, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
            },
            body: body,
        });

        const data = await res.json();

        if (!res.ok || !data?.status) {
            setError(data?.error || 'Не удалось удалить');
            return false;
        }

        return true;
    } catch (err) {
        setError('Не удалось удалить');
        return false;
    }
}

function MyInput({ sx, error, ...props }) {
    return (
        <FormControl error={error}>
            <Input
                sx={{
                    px: 2,
                    py: 1.5,
                    boxShadow: 'none',
                    borderRadius: '50px',
                    ...sx
                }}
                {...props}
            />
            {error && (
                <FormHelperText>
                    <WarningCircle size={16} style={{ mr: 0.5 }} />
                    {error}
                </FormHelperText>
            )}
        </FormControl>
    );
}

function SettingsItem({ label, onClick, color = '' }) {
    return (
        <ListItemButton color="neutral" onClick={onClick} sx={{ py: 1, px: 3 }}>
            <Typography level="body-md" sx={{ flex: 1 }} color={color}>
                {label}
            </Typography>
            <CaretRight size={16} color="#999" />
        </ListItemButton>
    );
}

function PageSettingsProfile() {
    const navigation = useNavigate();

    const { user, loading, error } = useProfile();
    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        username: '',
        description: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Avatar
    const [errorAvatar, setErrorAvatar] = useState('');
    const [defaultAvatar, setDefaultAvatar] = useState('');
    const [loadedAvatars, setLoadedAvatars] = useState([]);
    const [loadingAvatars, setLoadingAvatars] = useState(false);

    const [cropAvatarOpen, setCropAvatarOpen] = useState(false);
    const [selectedAvatarImage, setSelectedAvatarImage] = useState(null);
    const fileInputAvatarRef = useRef(null);

    useEffect(() => {
        fetchGetImages(
            '/api/profile/avatars',
            setLoadedAvatars,
            setLoadingAvatars,
            setErrorAvatar
        );
    }, []);

    useEffect(() => {
        setDefaultAvatar(user?.avatar ?? '');
    }, [user, loading, error]);

    const handleClickAvatar = (avatar) => {
        fetchUpdateImage(
            '/api/profile/avatar',
            JSON.stringify({ avatar: avatar }),
            setErrorAvatar
        );

        setDefaultAvatar(avatar);
    }

    const onClickLoadImageAvatar = () => {
        fileInputAvatarRef.current?.click()
    }

    const onSaveLoadImageAvatar = async (croppedFile) => {
        setCropAvatarOpen(false);
        setSelectedAvatarImage(null);

        const data = await uploadCroppedImage(croppedFile, '/api/profile/avatar/upload', setErrorAvatar);

        if (data) {
            setDefaultAvatar(data);
            setLoadedAvatars(prev => [data, ...prev]);
        }
    };

    // Cap
    const [errorCap, setErrorCap] = useState('');
    const [defaultCap, setDefaultCap] = useState('');
    const [loadedCaps, setLoadedCaps] = useState([]);
    const [loadingCaps, setLoadingCaps] = useState(false);

    const [cropCapOpen, setCropCapOpen] = useState(false);
    const [selectedCapImage, setSelectedCapImage] = useState(null);
    const fileInputCapRef = useRef(null);

    useEffect(() => {
        fetchGetImages(
            '/api/profile/caps',
            setLoadedCaps,
            setLoadingCaps,
            setErrorCap
        );
    }, []);

    useEffect(() => {
        setDefaultCap(user?.cap ?? '');
    }, [user, loading, error]);

    const handleClickCover = (cover) => {
        fetchUpdateImage(
            '/api/profile/cap',
            JSON.stringify({ cap: cover }),
            setErrorCap
        );

        setDefaultCap(cover);
    }

    const onClickLoadImageCap = () => {
        fileInputCapRef.current?.click()
    }

    const onSaveLoadImageCap = async (croppedFile) => {
        setCropCapOpen(false);
        setSelectedCapImage(null);

        const data = await uploadCroppedImage(croppedFile, '/api/profile/cap/upload', setErrorCap);

        if (data) {
            setDefaultCap(data);
            setLoadedCaps(prev => [data, ...prev]);
        }
    };

    // Удаление загруженных фотографий (аватар/шапка)
    // deleteTarget: { type: 'avatar' | 'cap', src }
    const [deleteTarget, setDeleteTarget] = useState(null);

    const requestDeleteAvatar = (src) => setDeleteTarget({ type: 'avatar', src });
    const requestDeleteCap = (src) => setDeleteTarget({ type: 'cap', src });

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        const { type, src } = deleteTarget;
        setDeleteTarget(null);

        if (type === 'avatar') {
            const ok = await deleteImage('/api/profile/avatar', JSON.stringify({ avatar: src }), setErrorAvatar);
            if (ok) {
                setLoadedAvatars(prev => prev.filter(item => item !== src));
                setDefaultAvatar(prev => (prev === src ? '' : prev));
            }
        } else {
            const ok = await deleteImage('/api/profile/cap', JSON.stringify({ cap: src }), setErrorCap);
            if (ok) {
                setLoadedCaps(prev => prev.filter(item => item !== src));
                setDefaultCap(prev => (prev === src ? '' : prev));
            }
        }
    };

    useEffect(() => {
        if (user) {
            setForm({
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                username: user?.username || '',
                description: user?.description || ''
            });
        }
    }, [user]);

    useEffect(() => {
        if (!user || !isDirty) return;

        const timeout = setTimeout(() => {
            saveForm();
        }, 500);

        return () => clearTimeout(timeout);
    }, [form]);

    const saveForm = async () => {
        try {
            setFieldErrors({});
            setIsSaving(true);
            setSaveError(null);
            const res = await apiFetch('/api/profile', {
                method: 'PATCH',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
                },
                body: JSON.stringify(form)
            });


            const data = await res.json();

            setSaveError(data.error);
            setFieldErrors({
                firstName: data?.fields?.firstName ?? null,
                lastName: data?.fields?.lastName ?? null,
                username: data?.fields?.username ?? null,
                description: data?.fields?.description ?? null,
            });

            if (!res.ok) {
                throw new Error(data.error || 'Ошибка при сохранении');
            }
        } catch (err) {
            //
        } finally {
            setIsSaving(false);
        }
    };

    const isValidUsername = (value) => {
        if (value === '') return true;
        const regex = /^[a-z0-9_\-$]{1,20}$/;
        return regex.test(value);
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;
        const newForm = { ...form, [field]: value };
        const newErrors = { ...fieldErrors };

        if (field === 'username') {
            if (value !== '' && !isValidUsername(value)) {
                newErrors.username = 'Недопустимые символы';
            } else {
                delete newErrors.username;
            }
        } else if (['firstName', 'lastName', 'description'].includes(field) && value.length > 255) {
            newErrors[field] = 'Поле не должно превышать 255 символов';
        } else {
            delete newErrors[field];
        }

        setFieldErrors(newErrors);
        setForm(newForm);
        setIsDirty(true);
    };

    return (
        <AppProfile title="Настройки профиля" desc="Настройки профиля в социальной сети Аквариум">
            <LayoutSettings header="Профиль">
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <ServerError />
                ) : user ? (
                    <>
                        {saveError && <Typography color="danger" mb={2}>{saveError}</Typography>}
                        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexDirection: 'column' }}>
                            <Typography level="body-xs" sx={{ textTransform: 'uppercase', pl: 2.15 }}>
                                Личные данные
                            </Typography>
                            <MyInput
                                placeholder="Имя"
                                value={form.firstName}
                                onChange={handleChange('firstName')}
                                error={fieldErrors?.firstName}
                            />
                            <MyInput
                                placeholder="Фамилия"
                                value={form.lastName}
                                onChange={handleChange('lastName')}
                                error={fieldErrors?.lastName}
                            />
                        </Box>

                        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexDirection: 'column' }}>
                            <Typography level="body-xs" sx={{ textTransform: 'uppercase', pl: 2.5 }}>Имя пользователя</Typography>
                            <MyInput
                                placeholder="superman"
                                value={form.username}
                                onChange={handleChange('username')}
                                error={fieldErrors?.username}
                            />
                            <Typography level="body-xs" sx={{ pl: 2.5 }}>
                                Может содержать только латинские буквы в нижнем регистре и цифры.
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 3, display: 'flex', gap: 1, flexDirection: 'column' }}>
                            <Typography level="body-xs" sx={{ textTransform: 'uppercase', pl: 2.5 }}>Описание профиля</Typography>
                            <MyInput
                                placeholder="Расскажите о себе..."
                                value={form.description}
                                onChange={handleChange('description')}
                                error={fieldErrors?.description}
                            />
                            <Typography level="body-xs" sx={{ pl: 2.5 }}>
                                Краткая информация о вас, к примеру возраст, город проживания, сфера деятельности.
                            </Typography>
                        </Box>

                        <Stack spacing={4} mb={3}>
                            {/* Объяснение перед выбором картинок: связь «есть
                                обложка → профиль по центру» иначе неочевидна. */}
                            <ProfileLayoutHint hasCap={Boolean(defaultCap)} />

                            {loadingAvatars ? (
                                <CircularProgress />
                            ) : (
                                <HorizontalScrollAppearanceList
                                    title="Аватар"
                                    size="256x256"
                                    error={errorAvatar}

                                    ViewImage={ImageOptionAvatar}
                                    ViewImageLoad={ImageOptionLoadAvatar}

                                    images={[...loadedAvatars, ...defaultAvatars]}
                                    defaultImage={defaultAvatar}

                                    onClickImage={handleClickAvatar}
                                    onClickImageLoad={onClickLoadImageAvatar}

                                    deletableImages={loadedAvatars}
                                    onDeleteImage={requestDeleteAvatar}
                                />
                            )}

                            <input
                                ref={fileInputAvatarRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={async (e) => {
                                    let file = e.target.files?.[0]
                                    if (!file) return

                                    if (file.type === 'image/heic' || file.type === 'image/heif') {
                                        file = await convertHeicToJpg(file)
                                    }

                                    const url = URL.createObjectURL(file)

                                    setSelectedAvatarImage(url)
                                    setCropAvatarOpen(true)

                                    e.target.value = ''
                                }}
                            />

                            <AvatarCropModal
                                open={cropAvatarOpen}
                                image={selectedAvatarImage}
                                onClose={() => {
                                    setCropAvatarOpen(false)
                                    setSelectedAvatarImage(null)
                                }}
                                onSave={onSaveLoadImageAvatar}
                            />

                            {loadingCaps ? (
                                <CircularProgress />
                            ) : (
                                <HorizontalScrollAppearanceList
                                    title="Шапка"
                                    size="1280x720"
                                    error={errorCap}

                                    ViewImage={ImageOptionCap}
                                    ViewImageLoad={ImageOptionLoadCap}

                                    images={[...loadedCaps, ...defaultCovers]}
                                    defaultImage={defaultCap}

                                    onClickImage={handleClickCover}
                                    onClickImageLoad={onClickLoadImageCap}

                                    deletableImages={loadedCaps}
                                    onDeleteImage={requestDeleteCap}
                                />
                            )}

                            <input
                                ref={fileInputCapRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={async (e) => {
                                    let file = e.target.files?.[0]
                                    if (!file) return

                                    if (file.type === 'image/heic' || file.type === 'image/heif') {
                                        file = await convertHeicToJpg(file)
                                    }

                                    const url = URL.createObjectURL(file)

                                    setSelectedCapImage(url)
                                    setCropCapOpen(true)

                                    e.target.value = ''
                                }}
                            />

                            <CapCropModal
                                open={cropCapOpen}
                                image={selectedCapImage}
                                onClose={() => {
                                    setCropCapOpen(false)
                                    setSelectedCapImage(null)
                                }}
                                onSave={onSaveLoadImageCap}
                            />

                            <ModalConfirmDelete
                                open={!!deleteTarget}
                                setOpen={(open) => { if (!open) setDeleteTarget(null) }}
                                onDelete={confirmDelete}
                                title="Удалить фотографию?"
                                desc={deleteTarget?.type === 'cap'
                                    ? 'Шапка будет удалена без возможности восстановления.'
                                    : 'Аватарка будет удалена без возможности восстановления.'}
                            />
                        </Stack>

                        <Sheet variant="plain" sx={{ borderRadius: '24px', py: 1, my: 2 }}>
                            <List>
                                <SettingsItem
                                    label="Установить пароль"
                                    onClick={() => navigation('/settings/profile/password')}
                                />
                                {/* <SettingsItem
                                    label="Сменить почту"
                                    onClick={() => navigation('/settings/profile')}
                                /> */}
                            </List>
                        </Sheet>

                        <Sheet variant="plain" sx={{ borderRadius: '24px', py: 1, my: 2 }}>
                            <List>
                                <SettingsItem
                                    label="Удалить аккаунт"
                                    color="danger"
                                    onClick={() => navigation('/settings/profile/delete')}
                                />
                            </List>
                        </Sheet>
                    </>
                ) : null}
            </LayoutSettings>
        </AppProfile>
    );
}

export default PageSettingsProfile;
